// RegisterTabulka.jsx — REK_REGISTER_V1 (21. 10. 2026; Mirko 2800: „filtre podla stlpcov ako chcem“; GO 2804)
// REK_REGISTER_V1C (21. 10. 2026, Mirko: „screen musi byt celý zobrazený treba rolovat zlava do prava to nemože byt“):
// tabuľka má pevné rozloženie (table-layout: fixed) — stĺpce sa delia o šírku podľa váh `w`, dlhý text sa skráti s „…“ a celým
// znením v title; na žiadnej šírke nevzniká vodorovné rolovanie. Dátumové filtre sú kompaktné.
// SPOLOČNÝ DIEL (ústava 9.D.2 — systémové, 0.J): tabuľka registra s FILTROM V HLAVIČKE KAŽDÉHO STĹPCA
// (výber z hodnôt s počtom · text „obsahuje“ · dátum od–do), VÝBEROM STĹPCOV (131.3: predvolené + zapínateľné,
// výber sa pamätá na používateľa) a jednou výškou riadku (66.9). Stavia na Table.jsx (66.3, klávesy 54.2).
// Všeobecný — bez logiky reklamácií; ďalšie registre (Došlé FA, Zákazky, Partneri) dostanú ten istý diel.
//
//   const st = useStlpce('reklamacie', STLPCE)          // STLPCE: [{ k, n, f:'text'|'vyber'|'datum', v(r), iso?(r), on, mono?, farba?(r), w? (váha šírky) }]
//   const zaklad = rows.filter(r => st.prejde(r))        // filtre stĺpcov (počty vo filtroch stavu sa rátajú z tohto)
//   <RegisterTabulka st={st} rows={zobrazene} zdroj={zaklad} selectedId onSelect onOpen oznacene onOznac pasik stitok />
//   <FiltreTlacidlo st={st} /> <StlpceVyber st={st} />   // do jedného radu nad prácou
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { COLORS, TYPO, SPACE, RADIUS, norm } from '../theme.js'
import { Table, THead, TBody, TR, TH, TD } from './Table.jsx'
import { useAuth } from '../auth/AuthContext.jsx'

const FARBA = { err: COLORS.remove, warn: COLORS.warn || '#854F0B', ok: COLORS.add, gr: COLORS.muted }
const VYSKA = 34   // 66.9 — jedna výška riadku pre všetky bunky, aj so štítkom
const inpF = { fontFamily: 'inherit', fontSize: TYPO.sizes.xs, padding: '3px 6px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, width: '100%', minWidth: 0, background: COLORS.bg, height: 26, boxSizing: 'border-box' }

function jeAktivne(v) { return !!v && (typeof v === 'string' ? v !== '' : !!(v.od || v.do)) }
function skD(iso) { const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? (+m[3]) + '. ' + (+m[2]) + '. ' + m[1] : String(iso || '') }

export function useStlpce(obrazovka, definicia) {
  const { user } = useAuth()
  const kluc = 'matrix:stlpce:' + obrazovka + ':' + ((user && user.username) || '')
  const [viditelne, setViditelne] = useState(() => {
    try { const j = JSON.parse(localStorage.getItem(kluc) || 'null'); if (Array.isArray(j) && j.length) return j } catch (_) {}
    return definicia.filter(d => d.on).map(d => d.k)
  })
  useEffect(() => { try { localStorage.setItem(kluc, JSON.stringify(viditelne)) } catch (_) {} }, [kluc, viditelne])
  const [cf, setCf] = useState({})
  const [cfOn, setCfOn] = useState(true)
  const aktivne = useMemo(() => Object.keys(cf).filter(k => jeAktivne(cf[k])), [cf])
  const prejde = useCallback((r, bezStlpca) => aktivne.every(k => {
    if (k === bezStlpca) return true
    const c = definicia.find(x => x.k === k); if (!c) return true
    const v = cf[k]; const h = String(c.v(r) ?? '')
    if (c.f === 'text') return norm(h).includes(norm(String(v)))
    if (c.f === 'vyber') return h === String(v)
    if (c.f === 'datum') { const d = String((c.iso ? c.iso(r) : h) || '').slice(0, 10); if (v.od && d < v.od) return false; if (v.do && d > v.do) return false; return true }
    return true
  }), [aktivne, cf, definicia])
  const nastav = useCallback((k, v) => setCf(o => ({ ...o, [k]: v })), [])
  const zrus = useCallback(() => setCf({}), [])
  const stlpce = useMemo(() => definicia.filter(d => viditelne.includes(d.k)), [definicia, viditelne])
  const prepni = useCallback((k) => setViditelne(v => {
    const nove = v.includes(k) ? v.filter(x => x !== k) : [...v, k]
    return definicia.filter(d => nove.includes(d.k)).map(d => d.k)   // poradie stĺpcov drží definícia (131.3)
  }), [definicia])
  // PRIJMY_ZAKAZKA_V1 (Mirko 27. 10. 2026: „aby sa dalo triediť podobne ako u reklamácii“): klik na názov stĺpca = triedenie
  // vzostupne → zostupne → pôvodné poradie. Hodnota: c.s(r) (číslo, napr. suma) › c.iso(r) (dátum) › c.v(r) (text, slovenské poradie).
  const [trieda, setTrieda] = useState(null)
  const prepniTriedenie = useCallback((k) => setTrieda(t => (t && t.k === k) ? (t.smer === 1 ? { k, smer: -1 } : null) : { k, smer: 1 }), [])
  const zorad = useCallback((rows) => {
    if (!trieda) return rows
    const c = definicia.find(x => x.k === trieda.k); if (!c) return rows
    const hod = (r) => { const x = c.s ? c.s(r) : (c.iso ? c.iso(r) : c.v(r)); return x == null ? '' : x }
    return [...rows].sort((a, b) => {
      const x = hod(a), y = hod(b)
      const p = (typeof x === 'number' && typeof y === 'number') ? x - y : String(x).localeCompare(String(y), 'sk', { numeric: true })
      return p * trieda.smer
    })
  }, [trieda, definicia])
  const popis = useMemo(() => aktivne.map(k => {
    const c = definicia.find(x => x.k === k); const v = cf[k]
    return (c ? c.n : k) + ' = ' + (typeof v === 'string' ? v : [v.od ? 'od ' + skD(v.od) : '', v.do ? 'do ' + skD(v.do) : ''].filter(Boolean).join(' '))
  }), [aktivne, cf, definicia])
  return { definicia, stlpce, viditelne, prepni, cf, nastav, zrus, aktivne, popis, cfOn, setCfOn, prejde, trieda, prepniTriedenie, zorad }   // PRIJMY_ZAKAZKA_V1: trieda, prepniTriedenie, zorad
}

// tlačidlo Filtre — skryje/ukáže rad filtrov; nesie počet aktívnych
export function FiltreTlacidlo({ st }) {
  const n = st.aktivne.length
  return (
    <button type="button" onClick={() => st.setCfOn(v => !v)} title="Filtre v hlavičke stĺpcov — výber z hodnôt, text „obsahuje“, dátum od–do"
      style={{ ...chip, background: st.cfOn ? COLORS.nav : COLORS.bg, color: st.cfOn ? '#fff' : COLORS.text, borderColor: st.cfOn ? COLORS.nav : COLORS.line }}>
      Filtre{n > 0 && <span style={{ fontFamily: TYPO.mono, fontWeight: TYPO.weights.medium, marginLeft: 5, opacity: .85 }}>{n}</span>}
    </button>
  )
}

// tlačidlo Stĺpce — zoznam s odškrtávaním (131.3), výber sa ukladá na používateľa
export function StlpceVyber({ st }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const zavri = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const esc = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', zavri); document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', zavri); document.removeEventListener('keydown', esc) }
  }, [open])
  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button type="button" onClick={() => setOpen(o => !o)} title="Ktoré stĺpce vidieť — predvolené a zapínateľné (ústava 131.3)"
        style={{ ...chip, background: open ? COLORS.nav : COLORS.bg, color: open ? '#fff' : COLORS.text, borderColor: open ? COLORS.nav : COLORS.line }}>Stĺpce</button>
      {open && (
        <div style={{ position: 'absolute', top: 30, right: 0, zIndex: 40, background: COLORS.bg, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md,
          boxShadow: '0 8px 20px rgba(15,23,42,.12)', padding: '8px 10px', minWidth: 220, fontSize: TYPO.sizes.sm }}>
          <div style={{ fontSize: TYPO.sizes.xs, textTransform: 'uppercase', letterSpacing: '.04em', color: COLORS.muted, fontWeight: TYPO.weights.bold, margin: '2px 0 6px' }}>Stĺpce registra</div>
          {st.definicia.map(c => (
            <label key={c.k} style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '2px 0', cursor: 'pointer' }}>
              <input type="checkbox" checked={st.viditelne.includes(c.k)} onChange={() => st.prepni(c.k)} /> {c.n}
            </label>))}
          <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 6 }}>Výber sa pamätá pre teba.</div>
        </div>)}
    </span>
  )
}

const chip = { border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, padding: '4px 9px', fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center' }

function Filter({ c, st, zdroj }) {
  const v = st.cf[c.k]
  const akt = jeAktivne(v)
  const ram = akt ? { borderColor: COLORS.nav, background: COLORS.navBg || '#EFF6FF' } : {}
  if (c.f === 'text') return <input type="text" value={v || ''} onChange={e => st.nastav(c.k, e.target.value)} placeholder="obsahuje…" style={{ ...inpF, ...ram }} />
  if (c.f === 'vyber') {
    const m = {}; (zdroj || []).filter(r => st.prejde(r, c.k)).forEach(r => { const h = String(c.v(r) ?? ''); m[h] = (m[h] || 0) + 1 })
    const hodnoty = Object.keys(m).sort((a, b) => a.localeCompare(b, 'sk'))
    return (
      <select value={v || ''} onChange={e => st.nastav(c.k, e.target.value)} style={{ ...inpF, ...ram }}>
        <option value="">všetky</option>
        {hodnoty.map(h => <option key={h} value={h}>{h} ({m[h]})</option>)}
        {akt && !hodnoty.includes(String(v)) && <option value={v}>{v} (0)</option>}
      </select>)
  }
  if (c.f === 'datum') {
    const o = (v && typeof v === 'object') ? v : { od: '', do: '' }
    const di = { ...inpF, ...ram, height: 23, fontSize: 11, padding: '1px 3px' }
    return (
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <input type="date" value={o.od || ''} title="od" onChange={e => st.nastav(c.k, { ...o, od: e.target.value })} style={di} />
        <input type="date" value={o.do || ''} title="do" onChange={e => st.nastav(c.k, { ...o, do: e.target.value })} style={di} />
      </span>)
  }
  return null
}

export function RegisterTabulka({ st, rows, zdroj, celkom, rowKey = 'id', selectedId, onSelect, onOpen, oznacene, onOznac, pasik, stitok, prazdne = 'Nič nevyhovuje filtrom.' }) {
  const stlpce = st.stlpce
  const M = celkom != null ? celkom : (zdroj || rows).length
  rows = st.zorad ? st.zorad(rows) : rows   // PRIJMY_ZAKAZKA_V1: triedenie podľa stĺpca (bez triedenia ostáva poradie volajúceho)
  // REK_REGISTER_V1C — váhy šírok: stĺpce sa delia o 100 % šírky, tabuľka nikdy nepretečie (table-layout: fixed)
  const sucet = stlpce.reduce((a, c) => a + (Number(c.w) || 1), 0)
  const sirka = (c) => (100 * (Number(c.w) || 1) / sucet).toFixed(2) + '%'
  const bunkaTxt = { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }
  return (
    <div>
      {st.aktivne.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm, flexWrap: 'wrap', fontSize: TYPO.sizes.xs, color: COLORS.muted, marginBottom: 4 }}>
          <span>Výsledok <b style={{ color: COLORS.text, fontFamily: TYPO.mono, fontWeight: TYPO.weights.medium }}>{rows.length}</b> z <b style={{ color: COLORS.text, fontFamily: TYPO.mono, fontWeight: TYPO.weights.medium }}>{M}</b> · filtre stĺpcov: {st.popis.join(' · ')}</span>
          <button type="button" onClick={st.zrus} style={{ ...chip, padding: '2px 8px' }}>Zrušiť filtre</button>
        </div>)}
      <Table selectedRow={selectedId} onRowSelect={onSelect} onRowOpen={onOpen} style={{ overflow: 'hidden' }} tableStyle={{ tableLayout: 'fixed', width: '100%' }}>
        <colgroup>
          {onOznac && <col style={{ width: 28 }} />}
          {stlpce.map(c => <col key={c.k} style={{ width: sirka(c) }} />)}
        </colgroup>
        <THead>
          <TR>
            {onOznac && <TH style={{ padding: '6px 4px' }} />}
            {stlpce.map(c => (
              <TH key={c.k} align={c.vpravo ? 'right' : 'left'} onClick={st.prepniTriedenie ? () => st.prepniTriedenie(c.k) : undefined}
                style={{ padding: '6px 6px', fontSize: TYPO.sizes.xs, overflow: 'hidden', textOverflow: 'ellipsis', cursor: st.prepniTriedenie ? 'pointer' : undefined, userSelect: 'none' }}
                title={st.prepniTriedenie ? 'Triediť podľa: ' + c.n + ' (znova = opačne, tretí raz = pôvodné poradie)' : c.n}>
                {c.n}{st.trieda && st.trieda.k === c.k && <span style={{ color: COLORS.nav, marginLeft: 3 }}>{st.trieda.smer === 1 ? '▲' : '▼'}</span>}{jeAktivne(st.cf[c.k]) && <span title="filter aktívny" style={{ color: COLORS.nav, fontSize: 9, marginLeft: 4, verticalAlign: 'middle' }}>●</span>}
              </TH>))}
          </TR>
          {st.cfOn && (
            <tr>
              {onOznac && <th style={{ padding: '3px 2px', background: COLORS.bg, borderBottom: `2px solid ${COLORS.line}` }} />}
              {stlpce.map(c => (
                <th key={c.k} style={{ padding: '3px 3px', background: COLORS.bg, borderBottom: `2px solid ${COLORS.line}`, verticalAlign: 'top', textAlign: 'left', fontWeight: 'normal', overflow: 'hidden' }}>
                  <Filter c={c} st={st} zdroj={zdroj || rows} />
                </th>))}
            </tr>)}
        </THead>
        <TBody>
          {rows.length === 0 && (
            <tr><td colSpan={stlpce.length + (onOznac ? 1 : 0)} style={{ padding: 14, textAlign: 'center', color: COLORS.muted, fontSize: TYPO.sizes.sm }}>
              {prazdne} {st.aktivne.length > 0 && <button type="button" onClick={st.zrus} style={{ ...chip, padding: '2px 8px', marginLeft: 6 }}>Zrušiť filtre</button>}
            </td></tr>)}
          {rows.map(r => {
            const id = r[rowKey]; const vyb = String(id) === String(selectedId)
            const pas = pasik ? pasik(r) : null
            return (
              <TR key={id} rowKey={id} onClick={() => onSelect && onSelect(id)} onDoubleClick={() => onOpen && onOpen(id)}
                style={{ background: vyb ? (COLORS.navBg || '#EFF6FF') : undefined, boxShadow: vyb ? `inset 3px 0 0 ${COLORS.nav}` : (pas ? `inset 3px 0 0 ${pas}` : 'none') }}>
                {onOznac && (
                  <TD style={{ padding: '0 4px', height: VYSKA }} onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={!!(oznacene && oznacene.has(id))} onChange={() => onOznac(id)} style={{ accentColor: COLORS.nav, cursor: 'pointer', verticalAlign: 'middle' }} />
                  </TD>)}
                {stlpce.map((c, ix) => {
                  const h = c.v(r); const f = c.farba ? FARBA[c.farba(r)] : null; const txt = (h == null || h === '') ? '—' : String(h)
                  return (
                    <TD key={c.k} align={c.vpravo ? 'right' : 'left'} mono={!!c.mono}
                      style={{ padding: '0 6px', height: VYSKA, fontSize: TYPO.sizes.sm, color: f || COLORS.text, fontWeight: f ? TYPO.weights.semibold : TYPO.weights.normal, overflow: 'hidden' }}>
                      <span title={txt} style={bunkaTxt}>{txt}{ix === 0 && stitok && stitok(r)}</span>
                    </TD>)
                })}
              </TR>)
          })}
        </TBody>
      </Table>
    </div>
  )
}

export default RegisterTabulka
